// react
import { Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import {
	EntityPayload,
	EntityType,
	ErrorCause,
	LoanPayload,
	Option,
	Project,
	ProjectPayload,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextAccountsDesignated, ContextEntities } from '../../pages/Project/Project.context';

// mui
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Checkbox,
	FormControlLabel,
	Grid,
	InputLabel,
	SxProps,
	Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// fields
import { Select } from '../../fields/Select/Select';
import { SelectLocale } from '../../fields/SelectLocale/SelectLocale';

// forms
import { EntityOnboard } from './EntityOnboard/EntityOnboard';

// props
export interface FormOnboardProjectProps {
	onClose: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement | HTMLFormElement>) => void;
	onSuccess: (e: MouseEvent<HTMLFormElement>, project: Project) => void;
	project: ProjectPayload;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormOnboardProjectB = ({
	onClose,
	onSuccess,
	project,
	setIsLoading,
}: FormOnboardProjectProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { accountsDesignated: accounts } = useContext(ContextAccountsDesignated);
	const { entities } = useContext(ContextEntities);

	const entitiesPending = entities as EntityPayload[];

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [error, setError] = useState<string | null>(null);
	const [isV2Enabled, setIsV2Enabled] = useState(false);
	const [entityIdsExpanded, setEntityIdsExpanded] = useState<Set<string>>(new Set());
	const [entityIdsSelected, setEntityIdsSelected] = useState<Set<string>>(
		new Set(entitiesPending.map((e) => e.idWbg).filter((id): id is string => !!id))
	);
	const [selectedUsersByEntity, setSelectedUsersByEntity] = useState<Map<string, Set<string>>>(
		() => {
			const map = new Map<string, Set<string>>();
			// Initialize with all users selected for each entity (default behavior)
			entitiesPending.forEach((entity) => {
				if (entity.idWbg && entity.users) {
					const userIds = entity.users.map((user) => user.idWbg).filter((id): id is string => !!id);
					map.set(entity.idWbg, new Set(userIds));
				}
			});
			return map;
		}
	);

	// handlers
	const handleClickExpand = (entityId: string) => {
		setEntityIdsExpanded((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(entityId)) {
				newSet.delete(entityId);
			} else {
				newSet.add(entityId);
			}
			return newSet;
		});
	};

	const handleToggleAllEntities = (checked: boolean) => {
		if (checked) {
			const allIds = new Set(
				entitiesPending.map((e) => e.idWbg).filter((id): id is string => !!id)
			);
			setEntityIdsSelected(allIds);
		} else {
			setEntityIdsSelected(new Set());
			setSelectedUsersByEntity(new Map());
		}
	};

	const handleToggleAllUsers = (entity: EntityPayload, checked: boolean) => {
		const users = entity.users || [];
		setSelectedUsersByEntity((prev) => {
			const map = new Map(prev);
			if (checked) {
				const allUsers = new Set(users.map((user) => String(user.idWbg)).filter(Boolean));
				map.set(entity.idWbg || '', allUsers);
			} else {
				map.set(entity.idWbg || '', new Set());
			}
			return map;
		});
	};

	// define selected entities with selected users
	const entitiesSelected = entitiesPending
		.filter((e) => e.idWbg && entityIdsSelected.has(e.idWbg))
		.map((entity) => {
			// Include only the selected users for this entity
			const selectedUserIds = selectedUsersByEntity.get(entity.idWbg || '') || new Set();
			const selectedUsers =
				entity.users?.filter((user) => {
					return typeof user.idWbg === 'string' && selectedUserIds.has(user.idWbg);
				}) || [];

			return {
				...entity,
				users: selectedUsers,
			};
		});

	// vars
	const idForm = 'form-onboard-project';

	// vars
	const optionsEntities: Option[] = [];

	// define entities options
	for (let i = 0; i < entitiesSelected.length; i++) {
		const entity = entitiesSelected[i];

		optionsEntities.push({
			label: String(entity?.name),
			value: String(entity?.idWbg),
		});
	}

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// start loader
		setIsLoading(true);

		// hide error
		setError(null);

		try {
			const form = document.querySelector('#form-onboard-project') as HTMLFormElement;
			const fd = new FormData(form);
			const entries = Object.fromEntries(fd.entries());

			// check if loans exist
			if (!project.loans.length) {
				throw new Error(`The project must be associated with loans.`, {
					cause: {
						id: 'PROJECT_LOAN_NON_EXISTENT',
					},
				});
			}

			// check if entities exist
			if (!entitiesSelected.length) {
				throw new Error(`The project must be associated with at least one entity.`, {
					cause: {
						id: 'PROJECT_ENTITY_NON_EXISTENT',
					},
				});
			}

			// for each selected entity
			for (let e = 0; e < entitiesSelected.length; e++) {
				// define entity
				const entity = entitiesSelected[e];

				// check if the users have required fields
				if (entity.users) {
					for (const user of entity.users) {
						// check if first name exists
						if (!user.firstName) {
							throw new Error(`The user with the email ${user.email} is missing a first name.`, {
								cause: {
									id: 'USER_FIRST_NAME_NON_EXISTENT',
									details: {
										email: user.email,
										entity: entity.name,
									},
								},
							});
						}

						// check if last name exists
						if (!user.lastName) {
							throw new Error(`The user with the email ${user.email} is missing a last name.`, {
								cause: {
									id: 'USER_LAST_NAME_NON_EXISTENT',
									details: {
										email: user.email,
										entity: entity.name,
									},
								},
							});
						}

						// check if email exists
						if (!user.email) {
							const userFullName = `${user.firstName} ${user.lastName}`;

							throw new Error(`The user ${userFullName} is missing an email.`, {
								cause: {
									id: 'USER_EMAIL_NON_EXISTENT',
									details: {
										name: userFullName,
										entity: entity.name,
									},
								},
							});
						}
					}
				}
			}

			// add owner to accounts
			const payloadAccounts = accounts.map((account) => {
				return {
					...account,
					entities: [
						{
							idWbg: entries[`account-${account.idWbg}-owner`] as string,
							relationship: 'OWNER',
						},
					],
					status: 'ACTIVE',
				};
			});

			// add type to entities
			const payloadEntities = entitiesSelected.map((entity) => {
				return {
					...entity,
					type: entries[`entity-${entity.idWbg}-type`] as EntityType,
				};
			});

			const payloadLoans: Partial<LoanPayload>[] = project.loans.map((loan) => {
				return {
					amount: loan.amount,
					cancelled: loan.cancelled,
					code: loan.code,
					currency: loan.currency,
					dateApproved: loan.dateApproved ? loan.dateApproved : null,
					dateClosing: loan.dateClosing ? loan.dateClosing : null,
					dateEffective: loan.dateEffective ? loan.dateEffective : null,
					dateSigning: loan.dateSigning ? loan.dateSigning : null,
					disbursed: loan.disbursed,
					financier: loan.financier,
					name: loan.name,
					num: loan.num,
					status: loan.status,
					subLoanNum: loan.subLoanNum,
				};
			});

			// post onboarding
			const fetchOnboardig = await fetch(`${process.env.REACT_APP_API_URL}/onboard/project`, {
				method: 'post',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					accounts: payloadAccounts,
					country: project.country,
					entities: payloadEntities,
					loans: payloadLoans,
					project: {
						idWbg: project.idWbg,
						version: isV2Enabled ? '2' : '1',
						locale: entries.locale,
						name: project.name,
						status: 'ENABLED',
					},
					region: project.region,
					users: project.users,
				}),
			});

			const resOnboarding = await fetchOnboardig.json();

			if (resOnboarding.error) {
				throw new Error(resOnboarding.error.message, {
					cause: {
						id: resOnboarding.error.id,
						details: resOnboarding.error.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess(e, resOnboarding);
			}

			if (onClose) {
				onClose(e);
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause) {
				switch (cause.id) {
					case 'ACCOUNT_DUPLICATE':
						message = t('error.accountDuplicate', { id: cause.details.idWbg });
						break;

					case 'PRACTICE_DUPLICATE':
						message = t('error.practiceDuplicate', { id: project.practice.id });
						break;

					case 'LOAN_DUPLICATE':
						message = t('error.loanDuplicate', {
							financier: cause.details.financier,
							num: cause.details.num,
						});
						break;

					case 'LOAN_ENTITY_DUPLICATE':
						message = t('error.loanEntityDuplicate');
						break;

					case 'PROJECT_ENTITY_NON_EXISTENT':
						message = t('error.projectEntityNonExistent');
						break;

					case 'PROJECT_LOAN_DUPLICATE':
						message = t('error.projectLoanDuplicate');
						break;

					case 'PROJECT_LOAN_NON_EXISTENT':
						message = t('error.projectLoanNonExistent');
						break;

					case 'REGION_DUPLICATE':
						message = t('error.regionDuplicate', { id: project.region.id });
						break;

					case 'USER_DUPLICATE':
						message = t('error.userDuplicate', { email: cause.details.email });
						break;

					case 'USER_EMAIL_NON_EXISTENT':
						message = t('error.userEmailNonExistent', {
							entity: cause.details.entity,
							name: cause.details.name,
						});
						break;

					case 'USER_FIRST_NAME_NON_EXISTENT':
						message = t('error.userFirstNameNonExistent', {
							email: cause.details.email,
							entity: cause.details.entity,
						});
						break;

					case 'USER_LAST_NAME_NON_EXISTENT':
						message = t('error.userLastNameNonExistent', {
							email: cause.details.email,
							entity: cause.details.entity,
						});
						break;

					default:
						message = t('error.server');
				}
			}

			setError(message);
		} finally {
			// end loader
			setIsLoading(false);
		}
	};

	return (
		<form id={idForm} onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography fontWeight={600}>{t('language')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<SelectLocale
						defaultValue="en-US"
						id={`${idForm}-locale`}
						name="locale"
						required={true}
					/>
				</Grid>
				<Grid item xs={12}>
					{' '}
					<Typography fontWeight={600}>{t('entities')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<Box
						sx={(theme) => {
							return {
								borderColor: theme.palette.divider,
								borderRadius: 2,
								borderStyle: 'solid',
								borderWidth: 1,
								overflow: 'hidden',
							};
						}}>
						<Grid container>
							<Grid item xs={12}>
								<Box
									sx={(theme) => {
										return {
											backgroundColor: theme.palette.brand.grey[100],
											borderBottomColor: theme.palette.divider,
											borderBottomStyle: 'solid',
											borderBottomWidth: 1,
											padding: theme.spacing(2),
										};
									}}>
									<Grid alignItems="center" container spacing={2}>
										<Grid item xs={6}>
											<Grid alignItems="center" container spacing={1}>
												<Grid item alignItems="center" display="flex">
													<Checkbox
														checked={entityIdsSelected.size === entitiesPending.length}
														id={`${idForm}-select-all-entities`}
														onChange={(e) => handleToggleAllEntities(e.target.checked)}
														sx={{ p: 0 }}
													/>
												</Grid>
												<Grid item flex={1}>
													<InputLabel
														htmlFor={`${idForm}-select-all-entities`}
														sx={{ fontWeight: 600, m: 0 }}>
														{t('entity')}
													</InputLabel>
												</Grid>
											</Grid>
										</Grid>
										<Grid item xs={6}>
											<Typography fontWeight={600}>{t('type')}</Typography>
										</Grid>
									</Grid>
								</Box>
							</Grid>
							{entitiesPending.length > 0 ? (
								entitiesPending
									.sort((a, b) => {
										// First sort by name
										if (a.name !== b.name) {
											return a.name > b.name ? 1 : -1;
										}
										// If names are equal, sort by idWbg for stability
										return (a.idWbg || '') > (b.idWbg || '') ? 1 : -1;
									})
									.map((entity, indexEntities) => {
										const isLast = indexEntities === entities.length - 1;
										const optionEntity = optionsEntities.find((ent) => ent.value === entity.idWbg);
										const isDisabled = optionEntity?.disabled;

										// Check if entity is selected using the Set
										const isChecked = entity.idWbg ? entityIdsSelected.has(entity.idWbg) : false;

										return (
											<Grid item key={`${entity.idWbg}-${isChecked}`} xs={12}>
												<Box
													sx={(theme) => {
														let sx: SxProps = {};

														if (!isLast) {
															sx = {
																...sx,
																borderBottomColor: theme.palette.divider,
																borderBottomStyle: 'solid',
																borderBottomWidth: 1,
															};
														}

														return sx;
													}}>
													<EntityOnboard
														disabled={isDisabled}
														entity={entity}
														isChecked={isChecked}
														isExpanded={entityIdsExpanded.has(entity.idWbg || '')}
														onChangeAllUsers={handleToggleAllUsers}
														onChangeEntity={(e, checked, entity) => {
															if (!entity.idWbg) return;

															// add or remove selected entity ID
															const newSelectedIds = new Set(entityIdsSelected);
															if (checked) {
																newSelectedIds.add(entity.idWbg);
															} else {
																newSelectedIds.delete(entity.idWbg);
															}

															setEntityIdsSelected(newSelectedIds);

															// Update user selections for this entity
															const newSelectedUsersByEntity = new Map(selectedUsersByEntity);
															if (checked) {
																// Select all users for this entity
																const allUserIds =
																	entity.users
																		?.map((user) => user.idWbg)
																		.filter((id): id is string => !!id) || [];
																newSelectedUsersByEntity.set(entity.idWbg, new Set(allUserIds));
															} else {
																// Deselect all users for this entity
																newSelectedUsersByEntity.set(entity.idWbg, new Set());
															}
															setSelectedUsersByEntity(newSelectedUsersByEntity);
														}}
														onChangeUser={(e, checked, user, entity) => {
															if (!entity.idWbg || typeof user.idWbg !== 'string') return;

															// Update the selectedUsersByEntity map
															const newSelectedUsersByEntity = new Map(selectedUsersByEntity);
															const entityUserIds =
																newSelectedUsersByEntity.get(entity.idWbg) || new Set();
															const newEntityUserIds = new Set(entityUserIds);

															if (checked) {
																newEntityUserIds.add(user.idWbg);
															} else {
																newEntityUserIds.delete(user.idWbg);
															}

															newSelectedUsersByEntity.set(entity.idWbg, newEntityUserIds);
															setSelectedUsersByEntity(newSelectedUsersByEntity);
														}}
														onClickExpand={handleClickExpand}
														selectedUserIds={
															selectedUsersByEntity.get(entity.idWbg || '') || new Set()
														}
													/>
												</Box>
											</Grid>
										);
									})
							) : (
								<Grid item xs={12}>
									<Alert
										severity="info"
										sx={(theme) => {
											return {
												alignItems: 'center',
												borderRadius: 0,
											};
										}}>
										{t('alert.projectNoEntities')}
									</Alert>
								</Grid>
							)}
						</Grid>
					</Box>
				</Grid>
				<Grid item xs={12}>
					<Typography fontWeight={600}>{t('accounts')}</Typography>
				</Grid>
				<Grid item xs={12}>
					<Box
						sx={(theme) => {
							return {
								border: `1px ${theme.palette.divider} solid`,
								borderRadius: 2,
								overflow: 'hidden',
							};
						}}>
						<Grid container>
							<Grid item xs={12}>
								<Box
									sx={(theme) => {
										return {
											backgroundColor: theme.palette.brand.grey[100],
											borderBottom: `1px ${theme.palette.divider} solid`,
											padding: theme.spacing(2),
											minWidth: '100%',
										};
									}}>
									<Grid alignItems="center" container spacing={2}>
										<Grid item xs={6}>
											<Grid alignItems="center" container spacing={1}>
												<Grid item flex={1}>
													<Typography fontWeight={600}>{t('account')}</Typography>
												</Grid>
											</Grid>
										</Grid>
										<Grid item xs={6}>
											<Typography fontWeight={600}>{t('owner')}</Typography>
										</Grid>
									</Grid>
								</Box>
							</Grid>
							{accounts.length > 0 ? (
								accounts.map((account, indexAccounts) => {
									const isLast = indexAccounts === accounts.length - 1;
									return (
										<Grid item key={`project-account-${account.idWbg}-${indexAccounts}`} xs={12}>
											<Box
												sx={(theme) => {
													const styles: SxProps = {
														padding: theme.spacing(2),
														minWidth: '100%',
													};

													if (!isLast) {
														styles.borderBottom = `1px ${theme.palette.divider} solid`;
													}
													return styles;
												}}>
												<Grid alignItems="center" container spacing={2}>
													<Grid item xs={6}>
														<Typography fontWeight={600}>{account.name}</Typography>
													</Grid>
													<Grid item xs={6}>
														<Select
															hasNoneOption={false}
															id={`${idForm}-account-${account.idWbg}-owner`}
															name={`account-${account.idWbg}-owner`}
															options={optionsEntities}
															required={true}
														/>
													</Grid>
												</Grid>
											</Box>
										</Grid>
									);
								})
							) : (
								<Grid item xs={12}>
									<Alert
										severity="info"
										sx={(theme) => {
											return {
												alignItems: 'center',
												borderRadius: 0,
											};
										}}>
										{t('alert.projectNoAccounts')}
									</Alert>
								</Grid>
							)}
						</Grid>
					</Box>
				</Grid>
				<Grid item xs={12}>
					<Alert
						severity="info"
						sx={(theme) => {
							return {
								alignItems: 'center',
							};
						}}>
						{t('alert.enableLoansAfterOnboard')}
					</Alert>
				</Grid>
				<Grid item xs={12}>
					<Accordion
						disableGutters
						sx={{
							boxShadow: 'none',
							border: 1,
							borderColor: 'divider',
							borderStyle: 'solid',
							borderRadius: 2,
							'&:before': { display: 'none' },
						}}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Typography fontWeight={600}>{t('advancedOptions')}</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<FormControlLabel
								control={
									<Checkbox
										checked={isV2Enabled}
										color="primary"
										onChange={(e) => setIsV2Enabled(e.target.checked)}
									/>
								}
								label={
									<Box>
										<Typography variant="body1" fontWeight={600}>
											{t('enableV2.label')}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{t('enableV2.hint')}
										</Typography>
									</Box>
								}
							/>
						</AccordionDetails>
					</Accordion>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
